import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import { createPortal } from "react-dom";

const API_URL = process.env.REACT_APP_API_URL;

/** Unwraps string JSON + API Gateway/Lambda `{ body }` wrappers so `success` / `data` read reliably */
function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d && d.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d;
      }
    }
    return d.body;
  }
  return d;
}

/** True if value looks like a MongoDB ObjectId string (24 hex chars). */
function isMongoObjectIdString(value) {
  if (value == null || value === "") return false;
  return /^[a-f\d]{24}$/i.test(String(value).trim());
}

function toDateInputValue(value) {
  if (!value) return "";
  const str = String(value).trim();
  // HTML date input needs YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  // API often returns DD-MM-YYYY or DD/MM/YYYY (e.g. "26-10-2026")
  const dmy = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmy) {
    const day = dmy[1].padStart(2, "0");
    const month = dmy[2].padStart(2, "0");
    const year = dmy[3];
    return `${year}-${month}-${day}`;
  }
  const date = new Date(str);
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const QUOTATION_STATUS_OPTIONS = [
  "Draft",
  "Pending",
  "Submitted",
  "Regret",
  "Hold",
  "Not Quoted - RFQ Closed",
];

function normalizeQuotationStatus(status) {
  const value = String(status || "").trim();
  if (QUOTATION_STATUS_OPTIONS.includes(value)) return value;
  return "Pending";
}

const ProcurementTrackerEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState(-1);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(-1);
  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState("");
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [selectedSupplierIndex, setSelectedSupplierIndex] = useState(-1);
  const [isSuppliersLoading, setIsSuppliersLoading] = useState(true);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [clientData, setClientData] = useState([]);
  const [isCustomersLoading, setIsCustomersLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [materialSearchTerm, setMaterialSearchTerm] = useState("");
  const [showMaterialDropdown, setShowMaterialDropdown] = useState(false);
  const [selectedMaterialIndex, setSelectedMaterialIndex] = useState(-1);
  const [isMaterialsLoading, setIsMaterialsLoading] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);

  const materialDescInputRef = useRef(null);

const [materialDescriptionSearchTerm, setMaterialDescriptionSearchTerm] =
  useState("");

const [showMaterialDescriptionDropdown, setShowMaterialDescriptionDropdown] =
  useState(false);

const [selectedMaterialDescriptionIndex, setSelectedMaterialDescriptionIndex] =
  useState(-1);

const [materialDescriptionDropdownPos, setMaterialDescriptionDropdownPos] =
  useState(null);

  const [formData, setFormData] = useState({
    prNo: "",
    clientReference: "",
    deliveryLocation: "",
    prSummary: "",
    dueDate: "",
    materialCode: "",
    materialDescription: "",
    hsnCode: "",
    uom: "",
    quantity: "",
    userId: "",
    basicPrice: "",
    gst: "",
    totalValue: "",
    quotePrice: "",
    supplierReference: "",
    procurementRemarks: "",
    technicalQueries: "",
    quotationStatus: "Pending",
    quoteReference: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  /** Inline validation: field name → message (no toast for client-side form errors) */
  const [fieldErrors, setFieldErrors] = useState({});
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState(new Set());
  const [replaceFilesByS3Key, setReplaceFilesByS3Key] = useState({});
  const [addedFiles, setAddedFiles] = useState([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Fetch active suppliers for Supplier Reference dropdown
  useEffect(() => {
    const fetchSuppliers = async () => {
      setIsSuppliersLoading(true);
      try {
        const payload = {
          action: "get-active-suppliers",
          Page: 1,
          Limit: 5000,
        };
        const response = await axios.post(API_URL, payload);
        const responseData = parseApiResponse(response.data);

        if (
          responseData?.success === true &&
          Array.isArray(responseData.data)
        ) {
          setSuppliers(responseData.data);
          setFilteredSuppliers(responseData.data);
        } else {
          toast.error(responseData?.message || "Failed to fetch suppliers", {
            autoClose: 2000,
          });
        }
      } catch (error) {
        console.error("Fetch suppliers error:", error);
        toast.error("Failed to load suppliers", { autoClose: 2000 });
      } finally {
        setIsSuppliersLoading(false);
      }
    };

    fetchSuppliers();
  }, []);

  useEffect(() => {
    const fetchClientData = async () => {
      // Client reference picker — customer lookup for PR edit; no customer_master screen access required.
      setIsCustomersLoading(true);

      try {
        const res = await axios.post(API_URL, {
          action: "get-all-customers",
        });

        const data = parseApiResponse(res.data);

        if (data?.success) {
          const customers = data?.data?.customers || data?.data || [];

          setClientData(Array.isArray(customers) ? customers : []);
        } else {
          setClientData([]);
        }
      } catch (err) {
        console.error(err);
        setClientData([]);
      } finally {
        setIsCustomersLoading(false);
      }
    };

    fetchClientData();
  }, []);

  const filteredCustomers = (clientData || []).filter((c) =>
    c.customerName?.toLowerCase().includes(customerSearchTerm.toLowerCase()),
  );

  const selectedCustomer = clientData.find(
    (c) => String(c._id) === String(formData.clientReference),
  );

  const locationOptions = React.useMemo(() => {
    if (!selectedCustomer) return [];

    return (selectedCustomer.contacts || []).map((c) => c.city).filter(Boolean);
  }, [selectedCustomer]);

  const filteredLocations = (locationOptions || []).filter((loc) =>
    loc.toLowerCase().includes(locationSearchTerm.toLowerCase()),
  );

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearchTerm(value);
    setShowCustomerDropdown(true);

    setFormData((prev) => ({
      ...prev,
      clientReference: "",
      deliveryLocation: "",
    }));

    setLocationSearchTerm("");
  };

  const handleCustomerSelect = (customer) => {
    setCustomerSearchTerm(customer.customerName);

    setFormData((prev) => ({
      ...prev,
      clientReference: customer._id,
      deliveryLocation: "",
    }));

    setLocationSearchTerm("");
    setShowCustomerDropdown(false);
  };

  useEffect(() => {
    const fetchMaterials = async () => {
      setIsMaterialsLoading(true);

      try {
        const res = await axios.post(API_URL, {
          action: "get-all-material",
        });

        const response = parseApiResponse(res.data);

        if (response?.success) {
          const list = response?.data?.materials || [];

          setMaterials(list);
          setFilteredMaterials(list);
        } else {
          setMaterials([]);
          setFilteredMaterials([]);
        }
      } catch (err) {
        console.error(err);
        setMaterials([]);
        setFilteredMaterials([]);
      } finally {
        setIsMaterialsLoading(false);
      }
    };

    fetchMaterials();
  }, []);

  const handleMaterialSearchChange = (e) => {
    const value = e.target.value;

    setMaterialSearchTerm(value);
    setShowMaterialDropdown(true);

    if (!value.trim()) {
      setFilteredMaterials(materials);
      return;
    }

    const filtered = materials.filter((m) => {
      const name = m.productName || m.materialName || "";

      return (
        m.materialCode?.toLowerCase().includes(value.toLowerCase()) ||
        name.toLowerCase().includes(value.toLowerCase())
      );
    });

    setFilteredMaterials(filtered);
  };

  const handleMaterialSelect = (material) => {
  setMaterialSearchTerm(material.materialCode);
  setMaterialDescriptionSearchTerm(material.productName || ""); // ADD THIS

  setFormData((prev) => ({
    ...prev,
    materialCode: material.materialCode || "",
    materialDescription: material.productName || "",
    hsnCode: material.hsnCode || "",
    uom: material.uom || "",
  }));

  setShowMaterialDropdown(false);
  setSelectedMaterialIndex(-1);
};

  const handleMaterialKeyDown = (e) => {
    if (!showMaterialDropdown || filteredMaterials.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedMaterialIndex((prev) =>
          prev < filteredMaterials.length - 1 ? prev + 1 : prev,
        );
        break;

      case "ArrowUp":
        e.preventDefault();
        setSelectedMaterialIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;

      case "Enter":
        e.preventDefault();
        if (selectedMaterialIndex >= 0) {
          handleMaterialSelect(filteredMaterials[selectedMaterialIndex]);
        }
        break;

      case "Escape":
        setShowMaterialDropdown(false);
        break;

      default:
        break;
    }
  };

  const handleCustomerKeyDown = (e) => {
    if (!showCustomerDropdown || filteredCustomers.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedCustomerIndex((prev) =>
          prev < filteredCustomers.length - 1 ? prev + 1 : prev,
        );
        break;

      case "ArrowUp":
        e.preventDefault();
        setSelectedCustomerIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;

      case "Enter":
        e.preventDefault();
        if (selectedCustomerIndex >= 0) {
          handleCustomerSelect(filteredCustomers[selectedCustomerIndex]);
        }
        break;

      case "Escape":
        setShowCustomerDropdown(false);
        break;

      default:
        break;
    }
  };

  const handleLocationSearchChange = (e) => {
    const value = e.target.value;
    setLocationSearchTerm(value);
    setShowLocationDropdown(true);

    setFormData((prev) => ({
      ...prev,
      deliveryLocation: "",
    }));
  };

  const handleLocationSelect = (loc) => {
    setLocationSearchTerm(loc);

    setFormData((prev) => ({
      ...prev,
      deliveryLocation: loc,
    }));

    setShowLocationDropdown(false);
  };

  const handleLocationKeyDown = (e) => {
    if (!showLocationDropdown || filteredLocations.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedLocationIndex((prev) =>
          prev < filteredLocations.length - 1 ? prev + 1 : prev,
        );
        break;

      case "ArrowUp":
        e.preventDefault();
        setSelectedLocationIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;

      case "Enter":
        e.preventDefault();
        if (selectedLocationIndex >= 0) {
          handleLocationSelect(filteredLocations[selectedLocationIndex]);
        }
        break;

      case "Escape":
        setShowLocationDropdown(false);
        break;

      default:
        break;
    }
  };

  // When active suppliers load (or chips are set from API), map supplier name → real ObjectId for `_id`.
  useEffect(() => {
    if (!suppliers.length) return;
    setSelectedSuppliers((prev) => {
      if (!prev.length) return prev;
      let changed = false;
      const next = prev.map((s) => {
        if (isMongoObjectIdString(s._id)) return s;
        const name = String(s.clientname || "").trim();
        if (!name) return s;
        const found = suppliers.find(
          (sup) =>
            String(sup.clientname || "")
              .trim()
              .toLowerCase() === name.toLowerCase(),
        );
        if (found && (found._id || found.id)) {
          changed = true;
          return {
            _id: String(found._id || found.id),
            clientname: s.clientname,
          };
        }
        return s;
      });
      return changed ? next : prev;
    });
  }, [suppliers, selectedSuppliers]);

  // Supplier references are stored in array in backend; we keep them in `selectedSuppliers`.

  useEffect(() => {
    fetchProcurement();
    fetchUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch users after procurement data is loaded
  useEffect(() => {
    if (formData.userId || !isFetching) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.userId, isFetching]);

  // Auto-calculate Total Value when Basic Price or Quantity changes
  useEffect(() => {
    const basicPrice = parseFloat(formData.basicPrice) || 0;
    const quantity = parseFloat(formData.quantity) || 0;

    if (basicPrice > 0 && quantity > 0) {
      const calculatedTotal = basicPrice * quantity;
      setFormData((prev) => ({
        ...prev,
        totalValue: calculatedTotal.toFixed(2),
      }));
    } else if (basicPrice === 0 || quantity === 0) {
      setFormData((prev) => ({
        ...prev,
        totalValue: "0.00",
      }));
    }
  }, [formData.basicPrice, formData.quantity]);

  const fetchUnits = async () => {
    setIsLoadingUnits(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-units",
        page: 1,
        limit: 500,
        sortBy: "unit",
        sortOrder: "asc",
      });
      const responseData = parseApiResponse(response.data);
      const list = Array.isArray(responseData?.data?.units)
        ? responseData.data.units
        : [];
      const activeUnits = list
        .map((item) => ({
          id: item?._id || item?.id || "",
          unit: String(item?.unit || "").trim(),
          status: String(item?.status || "").trim(),
        }))
        .filter((item) => item.unit)
        .filter(
          (item) => !item.status || item.status.toLowerCase() === "active",
        );

      setUnitOptions(responseData?.success ? activeUnits : []);
    } catch (error) {
      console.error("Fetch units error:", error);
      setUnitOptions([]);
    } finally {
      setIsLoadingUnits(false);
    }
  };

  // Fetch active users for dropdown (including admin, supervisor, and regular users)
  const fetchUsers = async () => {
    try {
      const payload = {
        action: "get-users",
        page: 1,
        limit: 100,
      };

      const response = await axios.post(API_URL, payload);
      const responseData = parseApiResponse(response.data);

      if (responseData?.success === true && responseData.users) {
        // Filter active users (includes all roles: admin, supervisor, user, etc.)
        const activeUsers = responseData.users.filter(
          (user) => user.status === "active",
        );

        // Sort users by name for better UX
        const sortedUsers = activeUsers.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });

        // If there's a currently assigned user that's not in active users, add them at the top
        if (formData.userId) {
          const isAssignedUserInList = sortedUsers.some(
            (user) => (user._id || user.id) === formData.userId,
          );

          if (!isAssignedUserInList) {
            // Find the assigned user in all users
            const assignedUser = responseData.users.find(
              (user) => (user._id || user.id) === formData.userId,
            );

            if (assignedUser) {
              // Add the assigned user to the top of the list
              setUsers([assignedUser, ...sortedUsers]);
              return;
            }
          }
        }

        setUsers(sortedUsers);
      }
    } catch (error) {
      console.error("Fetch users error:", error);
    }
  };

  const fetchProcurement = async () => {
    if (!id) {
      toast.error("Procurement ID is missing", { autoClose: 2000 });
      navigate("/ProcurementTracker");
      return;
    }

    setIsFetching(true);
    try {
      const payload = {
        action: "get-procurement-by-id",
        id: id,
      };

      const response = await axios.post(API_URL, payload);
      const responseData = parseApiResponse(response.data);

      if (responseData?.success === true && responseData.data) {
        const procurement = responseData.data;
        // Pre-populate selected suppliers from existing supplierReference (array or string)
        const referenceRaw = procurement.supplierReference;
        let parsedNames = [];
        if (Array.isArray(referenceRaw)) {
          parsedNames = referenceRaw
            .map((s) => String(s || "").trim())
            .filter(Boolean);
        } else if (typeof referenceRaw === "string") {
          parsedNames = referenceRaw
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        } else if (referenceRaw != null) {
          // Fallback for unexpected types (number/object/etc.)
          const asString = String(referenceRaw).trim();
          parsedNames = asString ? [asString] : [];
        }
        if (parsedNames.length > 0) {
          let supplierIdsFromApi = [];
          if (Array.isArray(procurement.supplierIds)) {
            supplierIdsFromApi = procurement.supplierIds;
          } else if (procurement.supplierId != null) {
            supplierIdsFromApi = Array.isArray(procurement.supplierId)
              ? procurement.supplierId
              : [procurement.supplierId];
          }
          const normalizedIds = supplierIdsFromApi.map((x) => {
            if (x && typeof x === "object" && x._id) return String(x._id);
            return x != null ? String(x) : "";
          });
          setSelectedSuppliers(
            parsedNames.map((name, i) => {
              const idCandidate = normalizedIds[i];
              const useId =
                idCandidate && isMongoObjectIdString(idCandidate)
                  ? idCandidate.trim()
                  : name;
              return { _id: useId, clientname: name };
            }),
          );
        } else {
          setSelectedSuppliers([]);
        }

        const client = clientData.find(
          (c) => String(c._id) === String(procurement.clientReferenceId),
        );

        const locations =
          client?.contacts?.map((c) => c.city).filter(Boolean) || [];

        setFormData({
          prNo: procurement.prNo || "",
          clientReference: procurement.clientReferenceId || "",
          deliveryLocation: procurement.deliveryLocation || locations[0] || "",
          prSummary: procurement.prSummary || "",
          dueDate: toDateInputValue(procurement.dueDate),
          materialCode: procurement.materialCode || "",
          materialDescription: procurement.materialDescription || "",
          hsnCode: procurement.hsnCode || "",
          uom: procurement.uom || "",
          quantity: procurement.quantity || "",
          userId: procurement.userId?._id || procurement.userId || "",
          basicPrice: procurement.basicPrice || "",
          gst: procurement.gst || "",
          totalValue: procurement.totalValue || "",
          quotePrice: procurement.quotePrice || "",
          // keep string field for backward compatibility / export, but source of truth is `selectedSuppliers`
          supplierReference: Array.isArray(procurement.supplierReference)
            ? procurement.supplierReference.join(", ")
            : procurement.supplierReference || "",
          procurementRemarks: procurement.procurementRemarks || "",
          technicalQueries: procurement.technicalQueries || "",
          quotationStatus: normalizeQuotationStatus(
            procurement.quotationStatus,
          ),
          quoteReference: procurement.quoteReference || "",
        });
        setMaterialSearchTerm(procurement.materialCode || "");
        setCustomerSearchTerm(procurement.clientReference || "");
        setLocationSearchTerm(procurement.deliveryLocation || "");

        // Attachments normalization (strings or objects)
        const rawAttachments =
          procurement.attachments ??
          procurement.s3Keys ??
          (procurement.s3Key ? [procurement.s3Key] : []);

        let normalized = [];
        if (Array.isArray(rawAttachments)) {
          normalized = rawAttachments
            .map((att) => {
              if (!att) return null;
              if (typeof att === "string") return { s3Key: att };
              if (typeof att === "object") {
                const s3Key = att.s3Key || att.key || "";
                if (!s3Key) return null;
                return {
                  s3Key,
                  originalFileName: att.originalFileName || att.fileName || "",
                  contentType: att.contentType || "",
                };
              }
              return null;
            })
            .filter(Boolean);
        }
        setMaterialDescriptionSearchTerm(
  procurement.materialDescription || ""
);
        setExistingAttachments(normalized);
        setAttachmentsToRemove(new Set());
        setReplaceFilesByS3Key({});
        setAddedFiles([]);
      } else {
        toast.error(
          responseData?.message || "Failed to fetch procurement details",
          { autoClose: 2000 },
        );
        navigate("/ProcurementTracker");
      }
    } catch (error) {
      console.error("Fetch procurement error:", error);
      if (error.response && error.response.data) {
        const errorData = parseApiResponse(error.response.data);
        const msg =
          typeof errorData === "string"
            ? errorData
            : errorData?.message ||
              "An error occurred while fetching procurement details";
        toast.error(msg, { autoClose: 2000 });
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          { autoClose: 2000 },
        );
      }
      navigate("/ProcurementTracker");
    } finally {
      setIsFetching(false);
    }
  };

  const clearFieldError = (fieldKey) => {
    setFieldErrors((prev) => {
      if (!prev[fieldKey]) return prev;
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  const SUBMITTED_ONLY_ERROR_KEYS = [
    "materialDescription",
    "uom",
    "basicPrice",
    "gst",
    "quotePrice",
    "supplierReference",
  ];

  

  const getFilteredMaterialDescriptions = () => {
  const search = materialDescriptionSearchTerm
    .trim()
    .toLowerCase();

  return materials.filter((m) => {
    return (
      (m.productName || "").toLowerCase().includes(search) ||
      (m.categoryName || "").toLowerCase().includes(search) ||
      (m.subcategoryName || "").toLowerCase().includes(search) ||
      (m.partNumber || "").toLowerCase().includes(search) ||
      (m.makeBrandOem || "").toLowerCase().includes(search)
    );
  });
};

const openMaterialDescriptionDropdown = useCallback(() => {
  if (isMaterialsLoading) return;

  if (materialDescInputRef.current) {
    const rect =
      materialDescInputRef.current.getBoundingClientRect();

    setMaterialDescriptionDropdownPos({
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
    });
  }

  setShowMaterialDescriptionDropdown(true);
}, [isMaterialsLoading]);

const handleMaterialDescriptionSearchChange = (value) => {
  setMaterialDescriptionSearchTerm(value);

  setFormData((prev) => ({
    ...prev,
    materialDescription: value,
  }));

  setSelectedMaterialDescriptionIndex(-1);

  openMaterialDescriptionDropdown();
};

const handleMaterialDescriptionSelect = (material) => {
  setMaterialDescriptionSearchTerm(material.productName || "");
  setMaterialSearchTerm(material.materialCode || ""); // ADD THIS

  setFormData((prev) => ({
    ...prev,
    materialCode: material.materialCode || "",
    materialDescription: material.productName || "",
    hsnCode: material.hsnCode || "",
    uom: material.uom || "",
  }));

  setShowMaterialDescriptionDropdown(false);
};

const handleMaterialDescriptionKeyDown = (e) => {
  if (!showMaterialDescriptionDropdown) return;

  const list = getFilteredMaterialDescriptions();

  if (e.key === "ArrowDown") {
    e.preventDefault();

    setSelectedMaterialDescriptionIndex((prev) =>
      prev < list.length - 1 ? prev + 1 : prev
    );
  }

  if (e.key === "ArrowUp") {
    e.preventDefault();

    setSelectedMaterialDescriptionIndex((prev) =>
      prev > 0 ? prev - 1 : 0
    );
  }

  if (e.key === "Enter") {
    e.preventDefault();

    if (
      selectedMaterialDescriptionIndex >= 0 &&
      list[selectedMaterialDescriptionIndex]
    ) {
      handleMaterialDescriptionSelect(
        list[selectedMaterialDescriptionIndex]
      );
    }
  }

  if (e.key === "Escape") {
    setShowMaterialDescriptionDropdown(false);
  }
};

useEffect(() => {
  if (!showMaterialDescriptionDropdown) return;

  const reposition = () => {
    openMaterialDescriptionDropdown();
  };

  window.addEventListener("scroll", reposition, true);
  window.addEventListener("resize", reposition);

  return () => {
    window.removeEventListener("scroll", reposition, true);
    window.removeEventListener("resize", reposition);
  };
}, [showMaterialDescriptionDropdown, openMaterialDescriptionDropdown]);

  // Handle quantity input - allow numbers and decimal point
  const handleQuantityChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, quantity: value }));
    }
  };

  // Handle price inputs - allow numbers and decimal point
  const handlePriceChange = (e, fieldName) => {
    const value = e.target.value;
    // Allow numbers and one decimal point
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, [fieldName]: value }));
      clearFieldError(fieldName);
    }
  };

  // Handle other input fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "quotationStatus" && value !== "Submitted") {
      setFieldErrors((prev) => {
        const next = { ...prev };
        SUBMITTED_ONLY_ERROR_KEYS.forEach((k) => {
          delete next[k];
        });
        return next;
      });
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name);
  };

  const handleNewAttachmentsChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setAddedFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const handleRemoveAddedFile = (idx) => {
    setAddedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const toggleRemoveExistingAttachment = (s3Key) => {
    setAttachmentsToRemove((prev) => {
      const next = new Set(prev);
      if (next.has(s3Key)) next.delete(s3Key);
      else next.add(s3Key);
      return next;
    });
  };

  const handleReplaceFileChange = (s3Key, file) => {
    if (!file) return;
    setReplaceFilesByS3Key((prev) => ({ ...prev, [s3Key]: file }));
    setAttachmentsToRemove((prev) => {
      const next = new Set(prev);
      next.delete(s3Key);
      return next;
    });
  };

  const getFileLabelFromAttachment = (att) => {
    if (!att) return "Attachment";
    if (att.originalFileName) return att.originalFileName;
    const key = att.s3Key || "";
    if (!key) return "Attachment";
    const parts = String(key).split("/");
    return parts[parts.length - 1] || key;
  };

  const putFileToS3 = async (uploadUrl, file) => {
    await axios.put(uploadUrl, file, {
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
    });
  };

  const getUploadPresignedUrls = async ({ files, createdBy }) => {
    const payload = {
      action: "get-price-upload-presigned-url",
      fileNames: files.map((f) => f.name),
      contentTypes: files.map((f) => f.type || "application/octet-stream"),
      createdBy,
    };
    const response = await axios.post(API_URL, payload);
    const responseData = parseApiResponse(response.data);

    if (responseData?.success !== true || !Array.isArray(responseData?.files)) {
      throw new Error(responseData?.message || "Failed to get upload URLs");
    }
    return responseData.files;
  };

  const buildAndUploadFinalAttachments = async ({ currentUserId }) => {
    const keptExisting = existingAttachments.filter(
      (att) => att?.s3Key && !attachmentsToRemove.has(att.s3Key),
    );

    const replaceEntries = Object.entries(replaceFilesByS3Key || {}).filter(
      ([oldKey, file]) => oldKey && file && !attachmentsToRemove.has(oldKey),
    );
    const replaceFiles = replaceEntries.map(([, file]) => file);
    const newFiles = addedFiles || [];

    // Replacements do NOT increase count; they overwrite existing items
    const finalCount = keptExisting.length + newFiles.length;
    if (finalCount > 10) {
      throw new Error("Maximum 10 attachments allowed");
    }

    const allFilesToUpload = [...replaceFiles, ...newFiles];
    if (allFilesToUpload.length === 0) {
      return keptExisting.map((a) => ({
        s3Key: a.s3Key,
        originalFileName: a.originalFileName,
        contentType: a.contentType,
      }));
    }

    setIsUploadingAttachments(true);
    try {
      const presignedFiles = await getUploadPresignedUrls({
        files: allFilesToUpload,
        createdBy: currentUserId,
      });

      // Upload in order
      for (let i = 0; i < presignedFiles.length; i++) {
        await putFileToS3(presignedFiles[i].uploadUrl, allFilesToUpload[i]);
      }

      const replacementMeta = new Map();
      replaceEntries.forEach(([oldKey], idx) => {
        const pf = presignedFiles[idx];
        replacementMeta.set(oldKey, {
          s3Key: pf.s3Key,
          originalFileName: pf.originalFileName || pf.fileName || "",
          contentType: pf.contentType || "",
        });
      });

      const replacedExisting = keptExisting.map((att) => {
        const rep = replacementMeta.get(att.s3Key);
        if (!rep) return att;
        return rep;
      });

      const newStartIdx = replaceFiles.length;
      const newUploaded = presignedFiles.slice(newStartIdx).map((pf) => ({
        s3Key: pf.s3Key,
        originalFileName: pf.originalFileName || pf.fileName || "",
        contentType: pf.contentType || "",
      }));

      return [...replacedExisting, ...newUploaded];
    } finally {
      setIsUploadingAttachments(false);
    }
  };

  // Supplier Reference multi-select handlers (like PriceTrackerAdd)
  const handleSupplierSearchChange = (e) => {
    const searchValue = e.target.value;
    setSupplierSearchTerm(searchValue);
    setShowSupplierDropdown(true);
    setSelectedSupplierIndex(-1);

    if (searchValue.trim() === "") {
      setFilteredSuppliers(suppliers);
    } else {
      const filtered = suppliers.filter((supplier) =>
        String(supplier.clientname || "")
          .toLowerCase()
          .includes(searchValue.toLowerCase()),
      );
      setFilteredSuppliers(filtered);
    }
  };

  const handleSupplierSelect = (supplier) => {
    const supplierId = supplier._id || supplier.id || "";
    const supplierName = supplier.clientname || "";
    if (!supplierName) return;

    setSupplierSearchTerm("");
    setShowSupplierDropdown(false);
    setSelectedSupplierIndex(-1);

    setSelectedSuppliers((prev) => {
      const exists = prev.some((s) => s.clientname === supplierName);
      if (exists) return prev;
      return [
        ...prev,
        { _id: supplierId || supplierName, clientname: supplierName },
      ];
    });
    clearFieldError("supplierReference");
  };

  const handleSupplierKeyDown = (e) => {
    if (!showSupplierDropdown || filteredSuppliers.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedSupplierIndex((prev) =>
          prev < filteredSuppliers.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedSupplierIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (
          selectedSupplierIndex >= 0 &&
          selectedSupplierIndex < filteredSuppliers.length
        ) {
          handleSupplierSelect(filteredSuppliers[selectedSupplierIndex]);
        } else if (filteredSuppliers.length === 1) {
          handleSupplierSelect(filteredSuppliers[0]);
        }
        break;
      case "Escape":
        setShowSupplierDropdown(false);
        setSelectedSupplierIndex(-1);
        break;
      default:
        break;
    }
  };

  const isEmptyOrInvalidDecimal = (value) => {
    if (value == null) return true;
    const s = String(value).trim();
    if (s === "") return true;
    const n = parseFloat(s);
    return Number.isNaN(n);
  };

  const hasSupplierReferenceForSubmit = useCallback(() => {
    if (selectedSuppliers.length > 0) return true;
    const fromText = String(formData.supplierReference || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return fromText.length > 0;
  }, [selectedSuppliers, formData.supplierReference]);

  useEffect(() => {
    setFieldErrors((prev) => {
      if (!prev.supplierReference) return prev;
      if (hasSupplierReferenceForSubmit()) {
        const next = { ...prev };
        delete next.supplierReference;
        return next;
      }
      return prev;
    });
  }, [selectedSuppliers, formData.supplierReference, hasSupplierReferenceForSubmit]);

  const fieldControlClass = (fieldKey, extra = "") => {
    const invalid = fieldErrors[fieldKey] ? " border-danger" : "";
    return `form-control${invalid}${extra ? ` ${extra}` : ""}`;
  };

  // Form validation (sets fieldErrors; no toast)
  const validateForm = () => {
    const errors = {};

    if (!formData.prNo.trim()) {
      errors.prNo = "PR Number is required";
    }
    if (!formData.userId) {
      errors.userId = "Please assign a user";
    }

    if (formData.quotationStatus === "Submitted") {
      if (!formData.materialDescription.trim()) {
        errors.materialDescription =
          "Material Description is required when Quotation Status is Submitted";
      }
      if (!formData.uom.trim()) {
        errors.uom = "UOM is required when Quotation Status is Submitted";
      }
      if (isEmptyOrInvalidDecimal(formData.basicPrice)) {
        errors.basicPrice =
          "Basic Price is required when Quotation Status is Submitted";
      }
      if (isEmptyOrInvalidDecimal(formData.gst)) {
        errors.gst = "GST % is required when Quotation Status is Submitted";
      }
      if (isEmptyOrInvalidDecimal(formData.quotePrice)) {
        errors.quotePrice =
          "Quote Price is required when Quotation Status is Submitted";
      }
      if (!hasSupplierReferenceForSubmit()) {
        errors.supplierReference =
          "Supplier Reference is required when Quotation Status is Submitted";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Get currentUserId from localStorage
    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => {
        navigate("/");
      }, 2000);
      return;
    }

    setIsLoading(true);

    try {
      const supplierReferencesToSend =
        selectedSuppliers && selectedSuppliers.length > 0
          ? selectedSuppliers.map((s) => s.clientname).filter(Boolean)
          : formData.supplierReference
            ? String(formData.supplierReference)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [];

      /** Resolve real Mongo ObjectIds — never send supplier name as supplierIds */
      const resolveSupplierIdsInOrder = () => {
        const ids = [];
        const rows =
          selectedSuppliers && selectedSuppliers.length > 0
            ? selectedSuppliers
            : supplierReferencesToSend.map((name) => ({
                _id: "",
                clientname: name,
              }));

        for (const s of rows) {
          const name = String(s.clientname || "").trim();
          let sid = s._id || s.id;
          if (isMongoObjectIdString(sid)) {
            ids.push(String(sid).trim());
            continue;
          }
          const found = suppliers.find(
            (sup) =>
              String(sup.clientname || "")
                .trim()
                .toLowerCase() === name.toLowerCase(),
          );
          if (found && (found._id || found.id)) {
            ids.push(String(found._id || found.id));
          } else if (name) {
            return {
              error: `Could not resolve supplier ID for "${name}". Choose suppliers from the list.`,
            };
          }
        }
        return { ids };
      };

      const resolved = resolveSupplierIdsInOrder();
      if (resolved.error) {
        toast.error(resolved.error, { autoClose: 3000 });
        setIsLoading(false);
        return;
      }
      const supplierIdsToSend = resolved.ids;

      const finalAttachments = await buildAndUploadFinalAttachments({
        currentUserId,
      });

      const selectedClient = clientData.find(
        (c) => String(c._id) === String(formData.clientReference),
      );

      const payload = {
        action: "update-procurement",
        requestUserId: currentUserId,
        id: id,
        prNo: formData.prNo.trim(),
        prSummary: formData.prSummary.trim(),
        clientReference: selectedClient?.customerName || "", // name
        clientReferenceId: selectedClient?._id || "",
        deliveryLocation: formData.deliveryLocation.trim(),
        dueDate: formData.dueDate || "",
        materialCode: formData.materialCode.trim(),
        materialDescription: formData.materialDescription.trim(),
        hsnCode: formData.hsnCode.trim(),
        uom: formData.uom.trim(),
        quantity: parseFloat(formData.quantity) || 0,
        userId: formData.userId || currentUserId,
        basicPrice: parseFloat(formData.basicPrice) || 0,
        gst: parseFloat(formData.gst) || 0,
        totalValue: parseFloat(formData.totalValue) || 0,
        quotePrice: parseFloat(formData.quotePrice) || 0,
        supplierReferences: supplierReferencesToSend,
        supplierIds: supplierIdsToSend,
        procurementRemarks: formData.procurementRemarks.trim(),
        technicalQueries: formData.technicalQueries.trim(),
        quotationStatus: normalizeQuotationStatus(formData.quotationStatus),
        quoteReference: formData.quoteReference.trim(),
        updatedBy: currentUserId,
        attachments: finalAttachments,
      };

      const response = await axios.post(API_URL, payload);
      const responseData = parseApiResponse(response.data);

      if (responseData?.success === true) {
        toast.success("Updated successfully", { autoClose: 2000 });
        setTimeout(() => {
          navigate("/ProcurementTracker");
        }, 2000);
      } else {
        toast.error(responseData?.message || "Failed to update procurement", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Update procurement error:", error);
      if (String(error?.message || "").includes("Maximum 10 attachments")) {
        toast.error("Maximum 10 attachments allowed", { autoClose: 2000 });
      } else if (error.response?.data) {
        const errorData = parseApiResponse(error.response.data);
        const msg =
          typeof errorData === "object" && errorData?.message
            ? errorData.message
            : error?.message || "An error occurred while updating procurement";
        toast.error(msg, { autoClose: 2000 });
      } else {
        toast.error(
          error?.message ||
            "Network error. Please check your connection and try again.",
          { autoClose: 2000 },
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">Edit Purchase Request </h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/ProcurementTracker">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Edit Purchase Entry</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/ProcurementTracker">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      {/* Basic Information Section */}
                      <div className="row mb-3">
                        <div className="col-12">
                          <h5 className="text-primary border-bottom pb-2">
                            <i className="fa-solid fa-info-circle mr-2"></i>
                            Basic Information
                          </h5>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          PR Number
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="prNo"
                            className={fieldControlClass("prNo")}
                            placeholder="PR Number"
                            value={formData.prNo}
                            readOnly
                            disabled
                            style={{
                              backgroundColor: "#e9ecef",
                              cursor: "not-allowed",
                            }}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Client Reference
                        </label>

                        <div className="col-12 col-md-8">
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={
                                isCustomersLoading
                                  ? "Loading clients..."
                                  : "Type to search client..."
                              }
                              value={customerSearchTerm}
                              onChange={handleCustomerSearchChange}
                              onKeyDown={handleCustomerKeyDown}
                              onFocus={() => setShowCustomerDropdown(true)}
                              onBlur={() => {
                                setTimeout(() => {
                                  setShowCustomerDropdown(false);
                                  setSelectedCustomerIndex(-1);
                                }, 200);
                              }}
                              autoComplete="off"
                              disabled={isCustomersLoading}
                              required
                            />
                            {isCustomersLoading && (
                              <div
                                style={{
                                  position: "absolute",
                                  right: "10px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                }}
                              >
                                <i className="fa fa-spinner fa-spin text-muted"></i>
                              </div>
                            )}
                            {showCustomerDropdown &&
                              !isCustomersLoading &&
                              filteredCustomers.length > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    zIndex: 1000,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  {filteredCustomers.map((c, index) => (
                                    <div
                                      key={c._id}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleCustomerSelect(c);
                                      }}
                                      style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #f0f0f0",
                                        backgroundColor:
                                          index === selectedCustomerIndex
                                            ? "#e9ecef"
                                            : "#fff",
                                      }}
                                      onMouseEnter={() =>
                                        setSelectedCustomerIndex(index)
                                      }
                                    >
                                      {c.customerName}
                                    </div>
                                  ))}
                                </div>
                              )}

                            {showCustomerDropdown &&
                              !isCustomersLoading &&
                              customerSearchTerm &&
                              filteredCustomers.length === 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    padding: "8px 12px",
                                    zIndex: 1000,
                                    color: "#6c757d",
                                  }}
                                >
                                  No clients found
                                </div>
                              )}
                          </div>

                          {formData.clientReference && (
                            <small className="form-text text-muted">
                              <i className="fa fa-check-circle text-success mr-1"></i>
                              Selected:{" "}
                              {
                                clientData.find(
                                  (c) => c._id === formData.clientReference,
                                )?.customerName
                              }
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Delivery Location
                        </label>

                        <div className="col-12 col-md-8">
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Type to search location..."
                              value={locationSearchTerm}
                              onChange={handleLocationSearchChange}
                              onKeyDown={handleLocationKeyDown}
                              onFocus={() => setShowLocationDropdown(true)}
                              onBlur={() => {
                                setTimeout(() => {
                                  setShowLocationDropdown(false);
                                  setSelectedLocationIndex(-1);
                                }, 200);
                              }}
                              autoComplete="off"
                              required
                            />

                            {showLocationDropdown &&
                              filteredLocations.length > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    zIndex: 1000,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  {filteredLocations.map((loc, index) => (
                                    <div
                                      key={index}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleLocationSelect(loc);
                                      }}
                                      style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #f0f0f0",
                                        backgroundColor:
                                          index === selectedLocationIndex
                                            ? "#e9ecef"
                                            : "#fff",
                                      }}
                                      onMouseEnter={() =>
                                        setSelectedLocationIndex(index)
                                      }
                                    >
                                      {loc}
                                    </div>
                                  ))}
                                </div>
                              )}

                            {showLocationDropdown &&
                              locationSearchTerm &&
                              filteredLocations.length === 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    padding: "8px 12px",
                                    zIndex: 1000,
                                    color: "#6c757d",
                                  }}
                                >
                                  No locations found
                                </div>
                              )}
                          </div>

                          {formData.deliveryLocation && (
                            <small className="form-text text-muted">
                              <i className="fa fa-check-circle text-success mr-1"></i>
                              Selected: {formData.deliveryLocation}
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          PR Summary
                        </label>
                        <div className="col-12 col-md-8">
                          <div
                            className="form-control"
                            style={{
                              backgroundColor: "#e9ecef",
                              cursor: "not-allowed",
                              minHeight: "38px",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {formData.prSummary || "-"}
                          </div>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Due Date
                        </label>
                        <div className="col-12 col-md-8">
                          <div
                            className="form-control"
                            style={{
                              backgroundColor: "#e9ecef",
                              cursor: "not-allowed",
                              minHeight: "38px",
                            }}
                          >
                            {formData.dueDate
                              ? new Date(
                                  `${formData.dueDate}T00:00:00`,
                                ).toLocaleDateString("en-GB")
                              : "-"}
                          </div>
                        </div>
                      </div>

                      {/* Material Details Section */}
                      <div className="row mb-3 mt-4">
                        <div className="col-12">
                          <h5 className="text-primary border-bottom pb-2">
                            <i className="fa-solid fa-box mr-2"></i>
                            Material Details
                          </h5>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Material Code
                        </label>
                        <div className="col-12 col-md-8">
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={
                                isMaterialsLoading
                                  ? "Loading materials..."
                                  : "Type to search material..."
                              }
                              value={materialSearchTerm}
                              onChange={handleMaterialSearchChange}
                              onKeyDown={handleMaterialKeyDown}
                              onFocus={() => setShowMaterialDropdown(true)}
                              onBlur={() => {
                                setTimeout(() => {
                                  setShowMaterialDropdown(false);
                                  setSelectedMaterialIndex(-1);
                                }, 200);
                              }}
                              autoComplete="off"
                              disabled={isMaterialsLoading}
                            />

                            {isMaterialsLoading && (
                              <div
                                style={{
                                  position: "absolute",
                                  right: "10px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                }}
                              >
                                <i className="fa fa-spinner fa-spin text-muted"></i>
                              </div>
                            )}

                            {showMaterialDropdown &&
                              !isMaterialsLoading &&
                              filteredMaterials.length > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    maxHeight: "220px",
                                    overflowY: "auto",
                                    background: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    zIndex: 1000,
                                    boxShadow: "0 2px 4px rgba(0,0,0,.1)",
                                  }}
                                >
                                  {filteredMaterials.map((material, index) => (
                                    <div
                                      key={material._id}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleMaterialSelect(material);
                                      }}
                                      onMouseEnter={() =>
                                        setSelectedMaterialIndex(index)
                                      }
                                      style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #eee",
                                        backgroundColor:
                                          index === selectedMaterialIndex
                                            ? "#e9ecef"
                                            : "#fff",
                                      }}
                                    >
                                      <strong>{material.materialCode}</strong>
                                    </div>
                                  ))}
                                </div>
                              )}

                            {showMaterialDropdown &&
                              !isMaterialsLoading &&
                              materialSearchTerm &&
                              filteredMaterials.length === 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    background: "#fff",
                                    border: "1px solid #ced4da",
                                    padding: "8px 12px",
                                    zIndex: 1000,
                                  }}
                                >
                                  No materials found
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Material Description
                        </label>
                        <div className="col-12 col-md-8">
                        <textarea
  ref={materialDescInputRef}
  className="form-control"
  rows={3}
  placeholder="Search Material Description..."
  value={materialDescriptionSearchTerm}
  onChange={(e) =>
    handleMaterialDescriptionSearchChange(e.target.value)
  }
  onKeyDown={handleMaterialDescriptionKeyDown}
  onFocus={openMaterialDescriptionDropdown}
  onBlur={() => {
    setTimeout(() => {
      setShowMaterialDescriptionDropdown(false);
      setSelectedMaterialDescriptionIndex(-1);
    }, 200);
  }}
/>

                        </div>
                      </div>
                      

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          HSN Code
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="hsnCode"
                            className="form-control"
                            placeholder="HSN Code"
                            value={formData.hsnCode}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          UOM
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            name="uom"
                            className={fieldControlClass("uom")}
                            value={formData.uom || ""}
                            onChange={handleChange}
                            disabled={isLoading || isLoadingUnits}
                          >
                            <option value="">
                              {isLoadingUnits
                                ? "Loading units..."
                                : "Select UOM"}
                            </option>
                            {formData.uom &&
                              !unitOptions.some(
                                (opt) =>
                                  opt.unit.toLowerCase() ===
                                  String(formData.uom).toLowerCase(),
                              ) && (
                                <option value={formData.uom}>
                                  {formData.uom}
                                </option>
                              )}
                            {unitOptions.map((opt) => (
                              <option
                                key={opt.id || opt.unit}
                                value={opt.unit}
                              >
                                {opt.unit}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Quantity
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="quantity"
                            className="form-control"
                            placeholder="Quantity"
                            value={formData.quantity}
                            onChange={handleQuantityChange}
                            onKeyPress={(e) => {
                              if (
                                !/[0-9.]/.test(e.key) &&
                                e.key !== "Backspace" &&
                                e.key !== "Delete" &&
                                e.key !== "Tab"
                              ) {
                                e.preventDefault();
                              }
                              if (
                                e.key === "." &&
                                String(formData.quantity || "").includes(".")
                              ) {
                                e.preventDefault();
                              }
                            }}
                            inputMode="decimal"
                          />
                        </div>
                      </div>

                      {/* User Assignment Section */}
                      <div className="row mb-3 mt-4">
                        <div className="col-12">
                          <h5 className="text-primary border-bottom pb-2">
                            <i className="fa-solid fa-user mr-2"></i>
                            User Assignment
                          </h5>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Assigned User
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            name="userId"
                            className={fieldControlClass("userId")}
                            value={formData.userId}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select User</option>
                            {users.map((user) => (
                              <option
                                key={user._id || user.id}
                                value={user._id || user.id}
                              >
                                {user.name} - {user.email}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Pricing Information Section */}
                      <div className="row mb-3 mt-4">
                        <div className="col-12">
                          <h5 className="text-primary border-bottom pb-2">
                            <i className="fa-solid fa-indian-rupee-sign mr-2"></i>
                            Supplier Quotes
                          </h5>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label
                          className={`col-12 col-md-4 col-form-label${
                            fieldErrors.supplierReference ? " text-danger" : ""
                          }`}
                        >
                          Supplier Reference
                        </label>
                        <div className="col-12 col-md-8">
                          <div
                            className={
                              fieldErrors.supplierReference
                                ? "border border-danger rounded p-2 mb-2"
                                : ""
                            }
                          >
                            {selectedSuppliers.length > 0 && (
                              <div className="mb-2">
                                {selectedSuppliers.map((s, idx) => (
                                  <span
                                    key={`${s.clientname}-${idx}`}
                                    className="badge badge-info mr-2 mb-1"
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: 550,
                                    }}
                                  >
                                    {s.clientname}
                                    <span
                                      className="ml-2 crsr_pntr"
                                      onMouseDown={(ev) => {
                                        ev.preventDefault();
                                        setSelectedSuppliers((prev) =>
                                          prev.filter((_, i) => i !== idx),
                                        );
                                      }}
                                      title="Remove"
                                    >
                                      <i className="fa-solid fa-xmark"></i>
                                    </span>
                                  </span>
                                ))}
                              </div>
                            )}

                            <div style={{ position: "relative" }}>
                              <input
                                type="text"
                                name="supplierReference"
                                className={fieldControlClass(
                                  "supplierReference",
                                )}
                                placeholder={
                                  isSuppliersLoading
                                    ? "Loading suppliers..."
                                    : "Type to search supplier and press Enter..."
                                }
                                value={supplierSearchTerm}
                                onChange={handleSupplierSearchChange}
                                onKeyDown={handleSupplierKeyDown}
                                onFocus={() => setShowSupplierDropdown(true)}
                                onBlur={() => {
                                  setTimeout(() => {
                                    setShowSupplierDropdown(false);
                                    setSelectedSupplierIndex(-1);
                                  }, 200);
                                }}
                                autoComplete="off"
                                disabled={isSuppliersLoading}
                              />

                              {showSupplierDropdown &&
                                !isSuppliersLoading &&
                                filteredSuppliers.length > 0 && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "100%",
                                      left: 0,
                                      right: 0,
                                      maxHeight: "200px",
                                      overflowY: "auto",
                                      backgroundColor: "#fff",
                                      border: "1px solid #ced4da",
                                      borderRadius: "4px",
                                      zIndex: 1000,
                                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                    }}
                                  >
                                    {filteredSuppliers.map(
                                      (supplier, index) => (
                                        <div
                                          key={supplier._id}
                                          onMouseDown={(ev) => {
                                            ev.preventDefault();
                                            handleSupplierSelect(supplier);
                                          }}
                                          style={{
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            borderBottom: "1px solid #f0f0f0",
                                            backgroundColor:
                                              index === selectedSupplierIndex
                                                ? "#e9ecef"
                                                : "#fff",
                                          }}
                                          onMouseEnter={() =>
                                            setSelectedSupplierIndex(index)
                                          }
                                        >
                                          {supplier.clientname}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}

                              {showSupplierDropdown &&
                                !isSuppliersLoading &&
                                supplierSearchTerm &&
                                filteredSuppliers.length === 0 && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "100%",
                                      left: 0,
                                      right: 0,
                                      backgroundColor: "#fff",
                                      border: "1px solid #ced4da",
                                      borderRadius: "4px",
                                      padding: "8px 12px",
                                      zIndex: 1000,
                                      color: "#6c757d",
                                      fontSize: "13px",
                                    }}
                                  >
                                    No suppliers found
                                  </div>
                                )}
                            </div>
                          </div>
                          <small className="form-text text-muted">
                            You can select multiple suppliers.
                          </small>
                          {fieldErrors.supplierReference && (
                            <small className="form-text text-danger d-block mt-1">
                              {fieldErrors.supplierReference}
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Attachments
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="file"
                            className="form-control"
                            multiple
                            onChange={handleNewAttachmentsChange}
                            disabled={isLoading || isUploadingAttachments}
                          />
                          <small className="form-text text-muted">
                            Max 10 files. Attachments are stored under{" "}
                            <span className="fwt_600">Price-attachment/</span>.
                          </small>

                          {isUploadingAttachments && (
                            <div
                              className="text-muted mt-2"
                              style={{ fontSize: "12px" }}
                            >
                              Uploading attachments...
                            </div>
                          )}

                          {(existingAttachments?.length || 0) > 0 && (
                            <div className="mt-3">
                              <div
                                className="fwt_600 mb-2"
                                style={{ fontSize: "13px" }}
                              >
                                Existing attachments
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "8px",
                                }}
                              >
                                {existingAttachments.map((att, idx) => {
                                  if (!att?.s3Key) return null;
                                  const isRemoved = attachmentsToRemove.has(
                                    att.s3Key,
                                  );
                                  const replacementFile =
                                    replaceFilesByS3Key?.[att.s3Key];
                                  const inputId = `replace-att-${idx}`;
                                  return (
                                    <div
                                      key={att.s3Key}
                                      className="p-2"
                                      style={{
                                        border: "1px solid #e5e7eb",
                                        borderRadius: "6px",
                                        background: "#fff",
                                      }}
                                    >
                                      <div
                                        className="d-flex justify-content-between align-items-start"
                                        style={{ gap: "10px" }}
                                      >
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div
                                            style={{
                                              fontSize: "13px",
                                              wordBreak: "break-word",
                                              opacity: isRemoved ? 0.5 : 1,
                                              textDecoration: isRemoved
                                                ? "line-through"
                                                : "none",
                                            }}
                                          >
                                            {getFileLabelFromAttachment(att)}
                                          </div>
                                          {replacementFile && !isRemoved && (
                                            <div
                                              className="text-muted mt-1"
                                              style={{
                                                fontSize: "12px",
                                                wordBreak: "break-word",
                                              }}
                                            >
                                              Replace with:{" "}
                                              <span className="fwt_600">
                                                {replacementFile.name}
                                              </span>
                                            </div>
                                          )}
                                        </div>

                                        <div
                                          className="d-flex"
                                          style={{ gap: "8px", flexShrink: 0 }}
                                        >
                                          <input
                                            id={inputId}
                                            type="file"
                                            style={{ display: "none" }}
                                            onChange={(e) =>
                                              handleReplaceFileChange(
                                                att.s3Key,
                                                e.target.files?.[0],
                                              )
                                            }
                                            disabled={
                                              isLoading ||
                                              isUploadingAttachments ||
                                              isRemoved
                                            }
                                          />
                                          <label
                                            htmlFor={inputId}
                                            className={`btn btn-sm ${isRemoved ? "btn-secondary" : "btn-outline-primary"}`}
                                            style={{ marginBottom: 0 }}
                                            title={
                                              isRemoved
                                                ? "Undo remove to replace"
                                                : "Replace this file"
                                            }
                                          >
                                            Replace
                                          </label>
                                          <button
                                            type="button"
                                            className={`btn btn-sm ${isRemoved ? "btn-warning" : "btn-outline-danger"}`}
                                            onClick={() =>
                                              toggleRemoveExistingAttachment(
                                                att.s3Key,
                                              )
                                            }
                                            disabled={
                                              isLoading ||
                                              isUploadingAttachments
                                            }
                                          >
                                            {isRemoved ? "Undo" : "Remove"}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {(addedFiles?.length || 0) > 0 && (
                            <div className="mt-3">
                              <div
                                className="fwt_600 mb-2"
                                style={{ fontSize: "13px" }}
                              >
                                New attachments
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "8px",
                                }}
                              >
                                {addedFiles.map((f, idx) => (
                                  <div
                                    key={`${f.name}-${idx}`}
                                    className="d-flex justify-content-between align-items-center p-2"
                                    style={{
                                      border: "1px dashed #cbd5e1",
                                      borderRadius: "6px",
                                      background: "#fbfdff",
                                      gap: "10px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        wordBreak: "break-word",
                                        flex: 1,
                                      }}
                                    >
                                      {f.name}
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleRemoveAddedFile(idx)}
                                      disabled={
                                        isLoading || isUploadingAttachments
                                      }
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Purchase Remarks
                        </label>
                        <div className="col-12 col-md-8">
                          <textarea
                            name="procurementRemarks"
                            className="form-control txtarea_hgt"
                            placeholder="Purchase Remarks"
                            value={formData.procurementRemarks}
                            onChange={handleChange}
                            rows={3}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Basic Price
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="basicPrice"
                            className={fieldControlClass("basicPrice")}
                            placeholder="0.00"
                            value={formData.basicPrice}
                            onChange={(e) => handlePriceChange(e, "basicPrice")}
                            inputMode="decimal"
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          GST %
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="gst"
                            className={fieldControlClass("gst")}
                            placeholder="0.00"
                            value={formData.gst}
                            onChange={(e) => handlePriceChange(e, "gst")}
                            inputMode="decimal"
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Total Value
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="totalValue"
                            className="form-control"
                            placeholder="0.00"
                            value={formData.totalValue}
                            readOnly
                            style={{
                              backgroundColor: "#e9ecef",
                              cursor: "not-allowed",
                            }}
                            title="Auto-calculated: Basic Price × Quantity"
                          />
                          <small className="form-text text-muted">
                            <i className="fa-solid fa-info-circle mr-1"></i>
                            Auto-calculated: Basic Price × Quantity
                          </small>
                        </div>
                      </div>

                      {/* Quotation Details Section */}
                      <div className="row mb-3 mt-4">
                        <div className="col-12">
                          <h5 className="text-primary border-bottom pb-2">
                            <i className="fa-solid fa-file-invoice mr-2"></i>
                            Customer Quotes
                          </h5>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Quotation Status
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            name="quotationStatus"
                            className="form-control"
                            value={normalizeQuotationStatus(
                              formData.quotationStatus,
                            )}
                            onChange={handleChange}
                          >
                            {QUOTATION_STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Quote Price
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="quotePrice"
                            className={fieldControlClass("quotePrice")}
                            placeholder="0.00"
                            value={formData.quotePrice}
                            onChange={(e) => handlePriceChange(e, "quotePrice")}
                            inputMode="decimal"
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Quote Reference
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="quoteReference"
                            className="form-control"
                            placeholder="Quote Reference"
                            value={formData.quoteReference}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Sales Remarks
                        </label>
                        <div className="col-12 col-md-8">
                          <textarea
                            name="technicalQueries"
                            className="form-control txtarea_hgt"
                            placeholder="Sales Remarks"
                            value={formData.technicalQueries}
                            onChange={handleChange}
                            rows={3}
                          />
                        </div>
                      </div>

                      {Object.keys(fieldErrors).length > 0 && (
                        <div className="form-group row">
                          <div className="col-12">
                            <div
                              className="alert alert-danger mb-0"
                              role="alert"
                            >
                              <div className="fwt_600 mb-2">
                                Please fix the following:
                              </div>
                              <ul
                                className="mb-0 pl-3"
                                style={{ fontSize: "13px" }}
                              >
                                {Object.entries(fieldErrors).map(
                                  ([key, msg]) => (
                                    <li key={key}>{msg}</li>
                                  ),
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={isLoading}
                      >
                        {isLoading ? "Updating..." : "Update"}
                      </button>
                    </div>
                    
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
       {showMaterialDescriptionDropdown &&
        materialDescriptionDropdownPos &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: materialDescriptionDropdownPos.top,
              left: materialDescriptionDropdownPos.left,
              width: materialDescriptionDropdownPos.width,
              maxHeight: "250px",
              overflowY: "auto",
              background: "#fff",
              border: "1px solid #ced4da",
              borderRadius: "4px",
              zIndex: 20000,
              boxShadow: "0 4px 10px rgba(0,0,0,.15)",
            }}
          >
            {getFilteredMaterialDescriptions().length > 0 ? (
              getFilteredMaterialDescriptions().map((material, index) => (
                <div
                  key={material._id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleMaterialDescriptionSelect(material);
                  }}
                  onMouseEnter={() =>
                    setSelectedMaterialDescriptionIndex(index)
                  }
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    background:
                      selectedMaterialDescriptionIndex === index
                        ? "#e9ecef"
                        : "#fff",
                    borderBottom: "1px solid #f1f1f1",
                  }}
                >
                  <strong>{material.productName}</strong>
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: "10px",
                  color: "#6c757d",
                }}
              >
                No materials found
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ProcurementTrackerEdit;
